import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryParam, NumberParam, StringParam, useQueryParams, ArrayParam, JsonParam } from 'use-query-params';

import {
    IProductCard,
    AvailibilityOption,
    Wishlist,
    Market,
    SortOption,
    LimiterOption,
    CategoryData
} from '../../types';

import {
    fetchBrowseResults,
    fetchSearchResults,
    constructorio
} from '../../api/constructor';

import { CssModuleType } from '@utils/cssModules';

import { useIsMobile } from '../../hooks';

import currentCssModule from './App.module.scss';

import Sidebar from '../../containers/Sidebar';
import Listing from '../../containers/Listing/Listing';
import MobileSidebar from '../../containers/MobileSidebar/MobileSidebar';
import ToolbarTopMobile from '../../containers/ToolbarTopMobile/ToolbarTopMobile';
import ToolbarTopDesktop from '../../containers/ToolbarTopDesktop/ToolbarTopDesktop';
import { ModalProvider } from '../../containers/ModalContext';
import Pagination from '../../components/Pagination/Pagination';

import { defaultLimiterOptions } from '../../mock/mockData';
import { Facet, FacetData } from '../../types';
import Loader from '../../components/Loader/Loader';
import Typography from '../../kit/atoms/Typography/Typography';
import {
    getBaseUrl,
    arraysAreEqual,
    transformSortData,
    getSortByValue
} from '../../helpers';

const App = () => {

    const st: CssModuleType = currentCssModule;
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // State

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [position, setPosition] = useQueryParam('position', NumberParam);
    
    const [pageNum, setPageNum] = useQueryParam('pageNum', NumberParam);
    const [sortOrder, setSortOrder] = useQueryParam('sortOrder', StringParam);
    const [sortBy, setSortBy] = useQueryParam('sortBy', StringParam);
    const [searchParams, setSearchParams] = useSearchParams();
    const [resultsPerPage, setResultsPerPage] = useState(() => searchParams.get('resultsPerPage') || 48);
    const [activeFilters, setActiveFilters] = useQueryParam('activeFilters', ArrayParam);
    const [filteredAvailabilityOptions, setFilteredAvailabilityOptions] = useQueryParam('filteredAvailabilityOptions', ArrayParam);
    const [search, setSearch] = useQueryParam('search', StringParam);
    const [ids, setIds] = useQueryParam('ids', StringParam);

    const getActiveFilters = () => {
        return activeFilters || [];
    }
    const getActiveObjectFilters = () => {
        const activeFilters = getActiveFilters();
        if (!activeFilters) return [];
        if (!accordionData.length) return [];
        const activeObjectFilters = activeFilters.map((af) => {
            const ad = accordionData?.find((fd) => af.includes(fd.name)); 
            const _af  = ad?.options?.find((opt) => af === `${ad.name}:${opt.value}`);
            const fd = {
                facet_name: ad.name,
                display_name: _af.display_name,
                value: _af.value
            };
            return fd;
        });
        console.log('activeObjectFilters', activeObjectFilters);
        return activeObjectFilters;
    }
    const getRequestActiveObjectFilters = () => {
        const activeFilters = getActiveFilters();
        console.log('activeFilters', activeFilters);
        if (!activeFilters) return [];
        const activeObjectFilters = activeFilters.map((af) => {
            const _af  = af.split(':');
            const fd = {
                facet_name: _af[0],
                value: _af[1]
            };
            return fd;
        });
        console.log('activeObjectFilters', activeObjectFilters);
        return activeObjectFilters;
    }

    const [market, setMarket] = useState<Market | null>(null);
    const [wishlist, setWishlist] = useState<Wishlist[] | null>(null);
    const [discount, setDiscount] = useState<number | null>(null);

    const [sortingOptions, setSortingOptions] = useState<SortOption[]>([]);
    const [categoryData, setCategoryData] = useState<CategoryData>();
    const [availabilityOptions, setAvailabilityOptions] = useState<
        AvailibilityOption[]
    >([]);

    const [tokenMatchCount, setTokenMatchCount] = useState<number | null>(null);

    const [productList, setProductList] = useState<IProductCard[]>([]);
    const [accordionData, setAccordionData] = useState<Facet[]>([]);
    const [limiterOptions, setLimiterOptions] = useState<LimiterOption[]>(
        defaultLimiterOptions
    );

    const [totalResults, setTotalResults] = useState<number | null>(null);
    const [totalPages, setTotalPages] = useState<number>(1);

    const [isSidebarOpen, setSidebarOpen] = useState(false);

    // Helper functions

    const scrollToPosition = () => {
        setTimeout(() => {
            window.scrollTo({ top: position, behavior: 'instant' })
        }, !position ? 0 : 1500);
    };

    // API calls

    const fetchBrowseResultsByItemsIds = (itemIds: string[], params = {}) => {
        return new Promise((resolve, reject) => {
            if (!itemIds?.length) {
                reject(new Error('Must contain a list of product IDs'));
                return;
            }

            constructorio.browse
                .getBrowseResultsForItemIds(itemIds, {
                    ...params
                })
                .then((response) => {
                    resolve(response.response);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    const getSearchResults = (term: string, props = {}) => {
        fetchSearchResults(term, props)
            .then((data) => {
                if (data.redirect?.data?.url) {
                    window.location.replace(data.redirect.data.url);
                    return;
                }
                updateDataOnRequest(data);
            })
            .catch(console.error)
            .finally(() => {
                scrollToPosition();
                setTimeout(() => setIsLoading(false));
            });
    };

    const getAllProducts = (
        filterName: string = 'group_id',
        filterValue: string = 'all',
        props = {}
    ) => {
        fetchBrowseResults(filterName, filterValue, {
            resultsPerPage,
            sortBy,
            sortOrder,
            ...props
        })
            .then((data) => {
                updateDataOnRequest(data);
            })
            .catch(console.error)
            .finally(() => {
                scrollToPosition();
                setTimeout(() => setIsLoading(false));
            });
    };

    const FilterProducts = (
        props,
        categoryId,
        collectionId,
        isCollection = false
    ) => {
        fetchBrowseResults(
            !isCollection ? 'group_id' : 'collection_id',
            !isCollection ? (categoryId ? categoryId : 'all') : collectionId,
            {
                resultsPerPage,
                sortBy,
                sortOrder,
                ...props
            }
        )
            .then((data) => {
                updateDataOnRequest(data);
            })
            .catch(console.error)
            .finally(() => {
                scrollToPosition();
                setTimeout(() => setIsLoading(false));
            });
    };

    const getProductsById = (idlist: string, props) => {
        const items = idlist.split('|');
        fetchBrowseResultsByItemsIds(items, {
            resultsPerPage,
            sortBy,
            sortOrder,
            ...props
        })
            .then((data) => {
                updateDataOnRequest(data);
            })
            .catch(console.error)
            .finally(() => {
                scrollToPosition();
                setTimeout(() => setIsLoading(false));
            });
    };

    const getProducts = () => {
        if (isLoading) return;

        setIsLoading(true);

        if (search?.length > 0) {
            getSearchResults(search, getRequestObject());
            return;
        }
        if (ids?.length) {
            getProductsById(ids, getRequestObject());
            return;
        }
        
        const app = document.querySelector(
            '#catalog_2023'
        ) as HTMLDivElement;
        const categoryId = app.getAttribute('data-categoryid');
        const collectionId = app.getAttribute('data_collectionid');

        if (categoryId) {
            getAllProducts(
                categoryId ? undefined : 'group_id',
                categoryId || 'all',
                getRequestObject()
            );
            return;
        }
        if (collectionId) {
            getAllProducts('collection_id', collectionId, getRequestObject());
            return;
        }

        FilterProducts(getRequestObject(), categoryId, collectionId, collectionId?.length > 0);
    }

    const getRequestObject = () => {
        const filtersObject = getRequestActiveObjectFilters().reduce((acc, filter) => {
            acc[filter.facet_name] = [
                ...(acc[filter.facet_name] || []),
                filter.value.replace(/["\\]/g, '')
            ];
            return acc;
        }, {});
        const filteredAvailability = filteredAvailabilityOptions || [];
        const availability = availabilityOptions.map((obj) => obj.value);

        const filters = {
            Availability_Filter: [],
            Visible_To: [],
            ...filtersObject
        };

        filters.Availability_Filter = filteredAvailability || availability;
        
        let marketValue = market?.MarketId?.Value;
        if (!marketValue) {
            const app = document.querySelector(
                '#catalog_2023'
            ) as HTMLDivElement;
            marketValue = JSON.parse(app.getAttribute('data-market'))
                ?.MarketId.Value;
        }

        if (marketValue) {
            filters.Visible_To = [marketValue];
        }

        const res = {
            resultsPerPage,
            sortBy,
            sortOrder,
            page: pageNum,
            filters: filters
        };
        
        return res;
    }

    // Handlers

    const handlePageChange = (page: number) => {
        setPosition(0);
        setPageNum(page);
        // getProducts();
    };

    const handleLimiterChange = (option) => {
        setPosition(0);
        setResultsPerPage(option.data);
        setPageNum(1);
        // getProducts();
    };

    const handleSortingChange = (option: SortOption) => {
        setPosition(0);
        setSortOrder(option.sort_order);
        setSortBy(option.data);
        setPageNum(1);
        // getProducts();
    };

    const handleAvailabilityOptions = (options: string[]) => {
        setPosition(0);
        setFilteredAvailabilityOptions(options);
        setPageNum(1);
        // getProducts();
    };

    const handleToggleFilter = (filter: FacetData) => {
        const afs = getActiveFilters();
        const isFilterActive = afs.some((af) => af === `${filter.facet_name}:${filter.value}`);
        const newActiveFilters = isFilterActive ? afs.filter((af) => af !== `${filter.facet_name}:${filter.value}`) : [...afs, `${filter.facet_name}:${filter.value}`];
        setPosition(0);
        setActiveFilters(newActiveFilters);
        setPageNum(1);
        // getProducts();
    };

    const handleRemoveFilter = (facetName: string, value: string) => {
        const afs = getActiveFilters();
        const newActiveFilters = afs.filter((af) => af !== `${facetName}:${value}`);
        setPosition(0);
        setActiveFilters(newActiveFilters);
        setPageNum(1);
        // getProducts();
    }

    const isItemActive = (facetName: string, value: string): boolean => {
        const aofs = getActiveObjectFilters();
        const afs = getActiveFilters();
        return afs.some(
            (filter) =>
                filter === `${facetName}:${value}`
        );
    };

    const clearAllFilters = () => {
        setPosition(0);
        setActiveFilters([]);
        setPageNum(1);
    };

    const handleWishlist = (variation_id: string) => {
        setWishlist((prevWishlist) => {
            if (!prevWishlist) return null;

            const itemIndex = prevWishlist.findIndex(
                (item) => item.Code === variation_id
            );

            if (itemIndex !== -1) {
                return [
                    ...prevWishlist.slice(0, itemIndex),
                    ...prevWishlist.slice(itemIndex + 1)
                ];
            } else {
                return [...prevWishlist, { Code: variation_id, Quantity: '1' }];
            }
        });
    };

    const calculateTotalPages = (totalResults) => {
        if (totalResults && resultsPerPage) {
            return Math.ceil(totalResults / resultsPerPage);
        }
        return 0
    };

    const updateDataOnRequest = (
        data
    ) => {
        setSortingOptions(transformSortData(data.sort_options, market || { MarketName: 'US_All' }));
        setCategoryData(data.groups);
        setAccordionData(data.facets);
        setProductList(data.results);

        setTokenMatchCount(data?.result_sources?.token_match?.count);

        if (!resultsPerPage) setResultsPerPage(48, 'replaceIn');

        setTotalResults(data.total_num_results);
        const newTotalPages = calculateTotalPages(data.total_num_results);
        setTotalPages(newTotalPages);

        const availability = data.facets?.find(
            (item) => item.name === 'Availability_Filter'
        );
        setAvailabilityOptions(availability.options);
        let filteredAvailability = filteredAvailabilityOptions;
        filteredAvailability = !filteredAvailability?.length ? availability?.options?.map(ao => ao.value) : filteredAvailability;
        setFilteredAvailabilityOptions(filteredAvailability, 'replaceIn');
    };

    const setAppData = (app) => {
        if (!app) return;

        const wishlist = app.getAttribute('data-wishlists');
        if (wishlist) setWishlist(JSON.parse(wishlist));

        const discount = app.getAttribute('data-discount');
        if (discount) setDiscount(parseInt(JSON.parse(discount)));

        const market = app.getAttribute('data-market');
        if (market) setMarket(JSON.parse(market));
    };

    // Effects

    useEffect(() => {
        const app = document.querySelector('#catalog_2023') as HTMLDivElement;
        setAppData(app);
        getProducts();
    }, [
        pageNum, 
        sortOrder, 
        sortBy, 
        resultsPerPage, 
        activeFilters, 
        filteredAvailabilityOptions, 
        search,
        ids
    ]);

    //////

    useEffect(() => {
        const body = document.querySelector('body');
        const app = document.querySelector('#catalog_2023') as HTMLDivElement;

        if (isSidebarOpen) {
            body.style.overflowY = 'hidden';
            app.style.zIndex = '1040';
        } else {
            body.style.overflowY = 'auto';
            app.style.zIndex = '1029';
        }
    }, [isSidebarOpen]);

    const showSearchResultsHeading = () => (
        <Typography className={st['search-results-heading']}>
            {t('/Search/ShowingResultsFor', { total: totalResults })}{' '}
            {`"${search}"`}
        </Typography>
    );

    const showNoMatchResultsHeading = () => (
      <Typography className={st['search-results-heading']}>
            <div className={st['search-results-heading-spacing']}>{t('/Search/ShowingNoMatchingResultsForHeading')}</div>
            <p className={st['search-results-heading-spacing']}>{t('/Search/ShowingNoMatchingResultsForDescription')}</p>
      </Typography>  
    );

    const showNoResults = () => {
        const noResultsRecommendations = document.querySelector(
            '.product-recommendations-block.no-results > div'
        );

        if (noResultsRecommendations) {
            noResultsRecommendations.setAttribute(
                'init-recommendations',
                'true'
            );
        }

        return (
            <Typography className={st['no-results']}>
                {t('/Search/NoResults')} {search}.
            </Typography>
        );
    };

    const showMainPageContent = () => {
        return (
            <>
                {search ? (tokenMatchCount > 0 ? showSearchResultsHeading() : showNoMatchResultsHeading()) : ''}
                <Sidebar
                    facetsData={accordionData}
                    activeFilters={getActiveObjectFilters()}
                    clearAllFilters={clearAllFilters}
                    handleToggleFilter={handleToggleFilter}
                    handleRemoveFilter={handleRemoveFilter}
                    isItemActive={isItemActive}
                />
                <div className={st.content}>
                    {!isMobile ? (
                        <ToolbarTopDesktop
                            totalResults={totalResults}
                            sortingOptions={sortingOptions}
                            onSortingChange={handleSortingChange}
                            limiterOptions={limiterOptions}
                            currentLimit={`${resultsPerPage}`}
                            currentSorting={sortBy}
                            currentOrder={sortOrder}
                            filteredAvailabilityOptions={filteredAvailabilityOptions}
                            availabilityOptions={availabilityOptions}
                            handleAvailabilityOptions={handleAvailabilityOptions}
                            onLimiterChange={handleLimiterChange}
                        />
                    ) : (
                        <>
                            <ToolbarTopMobile
                                productsQty={totalResults}
                                filteredAvailabilityOptions={filteredAvailabilityOptions}
                                availabilityOptions={availabilityOptions}
                                sortingOptions={sortingOptions}
                                onSortingChange={handleSortingChange}
                                currentSorting={sortBy}
                                currentOrder={sortOrder}
                                setSidebarOpen={setSidebarOpen}
                                activeFilters={getActiveObjectFilters()}
                                clearAllFilters={clearAllFilters}
                                handleRemoveFilter={handleRemoveFilter}
                                handleAvailabilityOptions={
                                    handleAvailabilityOptions
                                }
                            />
                            <MobileSidebar
                                activeFilters={getActiveObjectFilters()}
                                isOpen={isSidebarOpen}
                                onClose={() => setSidebarOpen(false)}
                                limiterOptions={limiterOptions}
                                currentLimit={`${resultsPerPage}`}
                                onLimiterChange={handleLimiterChange}
                                facetsData={accordionData}
                                handleToggleFilter={handleToggleFilter}
                                isItemActive={isItemActive}
                                clearAllFilters={clearAllFilters}
                                handleRemoveFilter={handleRemoveFilter}
                            ></MobileSidebar>
                        </>
                    )}

                    <Listing
                        data={productList}
                        categoryData={categoryData}
                        handleWishlist={handleWishlist}
                    />
                    
                    <Pagination
                        pageNum={pageNum || 1}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </>
        );
    };

    return (
        <ModalProvider
            wishlist={wishlist}
            market={market}
            discount={discount}
        >
            <div className={st.main}>
                {isLoading ? (
                    <Loader />
                ) : !totalResults ? (
                    showNoResults()
                ) : (
                    showMainPageContent()
                )}
            </div>
        </ModalProvider>
    );
};

export default App;
